import React, {
  createContext, useContext, useState, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import injectSheet from 'react-jss/lib/injectSheet';
import Toast from '.';
import theme from '../../styles/theme';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

let toastId = 0;

const ToastContainer = ({ classes, toasts, removeToast }) => {
  if (toasts.length === 0) return null;

  return (
    <div className={classes.container}>
      {toasts.map(toast => (
        <div key={toast.id} className={classes.toastWrapper}>
          <Toast
            onDismiss={() => removeToast(toast.id)}
            {...toast}
          />
        </div>
      ))}
    </div>
  );
};

ToastContainer.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  toasts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      type: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    }),
  ).isRequired,
  removeToast: PropTypes.func.isRequired,
};

const styles = {
  container: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    padding: 8,
    zIndex: 9999,
    pointerEvents: 'none',

    [theme.breakpoints.up('md')]: {
      padding: '0 0 24px 0',
      gap: '12px',
    },
  },
  toastWrapper: {
    width: '100%',
    pointerEvents: 'auto',

    [theme.breakpoints.up('md')]: {
      width: 'auto',
    },
  },
};

const StyledToastContainer = injectSheet(styles)(ToastContainer);

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback(options => {
    const id = toastId;
    toastId += 1;
    const newToast = {
      id,
      type: 'neutral',
      dismissible: true,
      autoHide: true,
      autoHideDelay: 10000,
      ...options,
    };

    setToasts(prevToasts => [...prevToasts, newToast]);
    return id;
  }, []);

  const removeToast = useCallback(id => {
    setToasts(prevToasts => prevToasts.filter(toast => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (title, options = {}) => addToast({ title, ...options }),
    [addToast],
  );

  const showSuccess = useCallback(
    (title, options = {}) => addToast({ title, type: 'positive', ...options }),
    [addToast],
  );

  const showError = useCallback(
    (title, options = {}) => addToast({ title, type: 'negative', ...options }),
    [addToast],
  );

  const showNeutral = useCallback(
    (title, options = {}) => addToast({ title, type: 'neutral', ...options }),
    [addToast],
  );

  const value = {
    showToast,
    showSuccess,
    showError,
    showNeutral,
    removeToast,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <StyledToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
};

ToastProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ToastProvider;
